import { useRef, useState } from 'react';
import { authorColor, authorInitials } from '../domain/notepad';

// Transform-agnostic overlay for `box` clips.
//
// The layer is positioned (by its parent) exactly over the rendered content
// (a PDF page or an image). All coordinates are normalized [0,1] relative to
// the LAYER'S OWN bounding rect — so it works unchanged under pan/zoom (when
// mounted inside react-zoom-pan-pinch's TransformComponent) and under PDF page
// scaling, because the rect already reflects the on-screen size.
//
// Two modes:
//   - view:    render saved boxes; each is clickable to open its note.
//   - capture: a crosshair surface; drag to draw a rectangle, release to
//              create a clip via onCapture(normalizedBbox).
//
// See ``plans/ATTORNEY_NOTEPAD_PLAN.md`` (anchor.kind = "bbox").

const MIN_BOX_FRACTION = 0.01; // ignore accidental tiny drags

export default function BoxClipLayer({
  clips = [],
  mode = 'view',
  myUid = null,
  onCapture = null,
  onOpenClip = null,
}) {
  const layerRef = useRef(null);
  const [draft, setDraft] = useState(null);   // {x0,y0,x1,y1} normalized
  const dragging = useRef(false);

  const capturing = mode === 'capture';

  const toNorm = (e) => {
    const rect = layerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
  };

  const onMouseDown = (e) => {
    if (!capturing) return;
    // Stop the drag from reaching an ancestor pan handler (react-zoom-pan-pinch
    // on images) so we draw a box instead of panning the image.
    e.preventDefault();
    e.stopPropagation();
    const [x, y] = toNorm(e);
    dragging.current = true;
    setDraft({ x0: x, y0: y, x1: x, y1: y });
  };
  const onMouseMove = (e) => {
    if (!capturing || !dragging.current) return;
    e.stopPropagation();
    const [x, y] = toNorm(e);
    setDraft((d) => (d ? { ...d, x1: x, y1: y } : d));
  };
  const onMouseUp = () => {
    if (!capturing || !dragging.current) return;
    dragging.current = false;
    const d = draft;
    setDraft(null);
    if (!d) return;
    const x = Math.min(d.x0, d.x1);
    const y = Math.min(d.y0, d.y1);
    const w = Math.abs(d.x1 - d.x0);
    const h = Math.abs(d.y1 - d.y0);
    if (w < MIN_BOX_FRACTION || h < MIN_BOX_FRACTION) return;
    if (onCapture) onCapture([x, y, w, h]);
  };

  return (
    <div
      ref={layerRef}
      className={`np-box-layer ${capturing ? 'np-box-capturing' : ''}`}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {clips.map((clip) => {
        const bbox = clip.anchor?.bbox;
        if (!Array.isArray(bbox) || bbox.length < 4) return null;
        const [bx, by, bw, bh] = bbox;
        const color = authorColor(clip.author_id);
        const tip = clip.note || clip.quote || (clip.author_name || 'Clip');
        return (
          <div
            key={clip.id}
            className="np-box-overlay"
            style={{
              left: `${bx * 100}%`,
              top: `${by * 100}%`,
              width: `${bw * 100}%`,
              height: `${bh * 100}%`,
              borderColor: color,
              // Boxes are click targets in view mode; in capture mode they
              // must not intercept the drag.
              pointerEvents: capturing ? 'none' : 'auto',
            }}
            title={tip}
            onClick={(e) => { e.stopPropagation(); if (onOpenClip) onOpenClip(clip); }}
          >
            <span className="np-box-tag" style={{ background: color }}>
              {authorInitials(clip.author_name, clip.author_id)}
              {clip.note ? ' •' : ''}
            </span>
          </div>
        );
      })}

      {draft && (
        <div
          className="np-box-temp"
          style={{
            left: `${Math.min(draft.x0, draft.x1) * 100}%`,
            top: `${Math.min(draft.y0, draft.y1) * 100}%`,
            width: `${Math.abs(draft.x1 - draft.x0) * 100}%`,
            height: `${Math.abs(draft.y1 - draft.y0) * 100}%`,
          }}
        />
      )}
    </div>
  );
}
